import { z } from "zod";
/**
 * Stable error codes (CLAUDE.md §9). Clients branch on `code`, never on `title` or `detail`.
 * Modules add their own business codes (e.g. `INSUFFICIENT_STOCK`) here as they are built.
 */
export const errorCodeSchema = z.enum([
    "VALIDATION_FAILED",
    "UNAUTHENTICATED",
    "FORBIDDEN",
    "NOT_FOUND",
    "CONFLICT",
    "BUSINESS_RULE_VIOLATION",
    "RATE_LIMITED",
    "INTERNAL",
    "SERVICE_UNAVAILABLE",
    // Authentication (docs/specs/identity.md).
    "INVALID_CREDENTIALS",
    "INVALID_REFRESH_TOKEN",
    "ACCOUNT_LOCKED",
    "INVALID_OTP",
    // Roles and permissions (docs/specs/identity.md Part 2).
    "LAST_OWNER",
    "SYSTEM_ROLE_READ_ONLY",
    // HTTP idempotency (docs/specs/events-jobs.md).
    "IDEMPOTENCY_KEY_REUSED",
    // Files (docs/specs/files.md).
    "FILE_REJECTED",
    // Plans (docs/specs/platform.md Part 2) and locations (docs/specs/locations.md).
    "PLAN_LIMIT_REACHED",
    "FEATURE_NOT_IN_PLAN",
    // Signup and legal acceptance (docs/specs/signup.md).
    "PHONE_REGISTERED",
    "EMAIL_REGISTERED",
    "LEGAL_OUTDATED",
    "LEGAL_ACCEPTANCE_REQUIRED",
    "SIGNUP_DISABLED",
    // Super Admin sign-in (docs/specs/super-admin.md Part 2).
    "TOTP_ENROLMENT_REQUIRED",
    "TOTP_NOT_ENROLLED",
    // Billing (docs/specs/billing.md).
    "DOWNGRADE_BLOCKED",
    "COUPON_INVALID",
    "TRX_ID_USED",
    "REQUEST_PENDING",
    "ACCOUNT_RESTRICTED",
    "ACCOUNT_SUSPENDED",
    "CLOSURE_CONFIRMATION_MISMATCH",
    "DATA_EXPORT_RUNNING",
    "PASSWORD_CHANGE_REQUIRED",
    "STAFF_ACCOUNT_SHARED",
    // HD-01b: the phone or e-mail already has an account; invite the person instead of adding them.
    "STAFF_HAS_ACCOUNT",
    "CANNOT_DISABLE_SELF",
    "LOCATION_IN_USE",
    // Catalog (docs/specs/catalog.md).
    "BARCODE_TAKEN",
    "NO_BARCODE",
    "SKU_TAKEN",
    "PRICE_ABOVE_MRP",
    "CATEGORY_IN_USE",
    "TOO_MANY_VARIANTS",
    "SEARCH_UNAVAILABLE",
    "IMPORT_NOT_VALIDATED",
    "IMPORT_FILE_UNREADABLE",
    // Inventory (docs/specs/inventory.md).
    "INSUFFICIENT_STOCK",
    "STOCK_NOT_TRACKED",
    "LOCATION_INACTIVE",
    "OPENING_STOCK_EXISTS",
    "PRODUCT_HAS_STOCK",
    "INVALID_TRANSITION",
    "COUNT_OVERLAP",
    "COUNT_EMPTY",
    "COUNT_TOO_LARGE",
    "SUPPLIER_ARCHIVED",
    "OVER_RECEIPT",
    "OVER_RETURN",
    "ALREADY_REVERSED",
    "NOTHING_DUE",
    "CREDIT_LIMIT_EXCEEDED",
    // Sales (docs/specs/sales.md).
    "VARIANT_NOT_SELLABLE",
    "INVALID_DISCOUNT",
    "NOT_A_DUPLICATE",
    "NO_PHONE",
    "REFUND_TOO_LARGE",
    // Payments (docs/specs/payments.md).
    "ACCOUNT_ARCHIVED",
    // Shipping (docs/specs/shipping.md).
    "AREA_IN_OTHER_ZONE",
    "COURIER_NOT_SUPPORTED",
    "COURIER_ACCOUNT_ARCHIVED",
    "NO_COURIER_ACCOUNT",
    "ORDER_NOT_SHIPPABLE",
    "SHIPMENT_EXISTS",
    "COURIER_CANNOT_CANCEL",
    "RTO_ALREADY_RECEIVED",
    "SHIPMENT_NOT_RETURNABLE",
    "PAYOUT_EXISTS",
    "PHONE_BLOCKED",
    // Accounting and reports (docs/specs/accounting.md, docs/specs/reporting.md).
    "CATEGORY_ARCHIVED",
    "REPORT_RANGE_TOO_LONG",
    // POS sync (docs/specs/pos-sync.md).
    "DEVICE_REVOKED",
    "REGISTER_HAS_DEVICE",
    "RESYNC_REQUIRED",
    "APP_UPGRADE_REQUIRED",
    // Sandbox (docs/specs/sandbox.md): no ready demo business right now (503 with Retry-After).
    "SANDBOX_BUSY",
    // Storefront (docs/specs/storefront.md).
    "DOMAIN_TAKEN",
    "DOMAIN_LIMIT_REACHED",
    "SLUG_TAKEN",
    "BOT_CHECK_FAILED",
    "COUPON_NOT_APPLICABLE",
    "COUPON_EXPIRED",
    "COUPON_MIN_ORDER",
    "COUPON_LIMIT_REACHED",
    // ST-25 (docs/specs/theme-demos.md): a theme demo shop takes no orders.
    "DEMO_SHOP",
    // Shopper accounts (docs/specs/shopper-accounts.md).
    "ACCOUNT_EXISTS",
    "SIGN_UP_OFF",
    "CODE_REQUIRED",
    "CODE_SIGN_IN_OFF",
    "PHONE_NOT_VERIFIED",
    "INVALID_GOOGLE_TOKEN",
    "GOOGLE_SIGN_IN_OFF",
    "PASSWORD_REQUIRED",
    // Digital products (docs/specs/digital.md).
    "ALREADY_IN_LIBRARY",
    "SIGN_IN_REQUIRED",
]);
/** One invalid input field. `path` is dot-separated (`lines.0.quantity`). */
export const fieldErrorSchema = z.object({
    path: z.string(),
    code: z.string(),
    message: z.string(),
});
/** RFC 9457 problem details, served as `application/problem+json`. */
export const problemDetailsSchema = z.object({
    /** Relative URI `/problems/<kebab-code>`; becomes absolute once docs have a domain. */
    type: z.string(),
    title: z.string(),
    status: z.number().int().min(400).max(599),
    code: errorCodeSchema,
    detail: z.string().optional(),
    instance: z.string(),
    requestId: z.string(),
    errors: z.array(fieldErrorSchema).optional(),
    /** With `PLAN_LIMIT_REACHED` / `FEATURE_NOT_IN_PLAN`: which key, its limit and the usage (B-59). */
    entitlement: z
        .object({ key: z.string(), limit: z.number().int().nullable(), usage: z.number().int() })
        .optional(),
});
export const PROBLEM_CONTENT_TYPE = "application/problem+json";
export function problemType(code) {
    return `/problems/${code.toLowerCase().replaceAll("_", "-")}`;
}
//# sourceMappingURL=problem.js.map